/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: Test runner for LogicFlow test cases — displays test cases, runs individual or bulk tests, shows inputs/expectations/results with pass/fail badges.
 */
import {
    Component,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {Subscription} from 'rxjs';
import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {LogicFlowService} from '../../../service/api/logic-flow.service';
import {FilterCriteria} from '../../../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../../../core/shared/misc/sorting';
import {QlService} from '../../../../../core/shared/ql/service/ql.service';
import {LogicFlowTestCaseDto} from '../../../model/dto/entity/logic-flow-test-case.dto';
import {LogicFlowTestCaseInputDto} from '../../../model/dto/entity/logic-flow-test-case-input.dto';
import {LogicFlowTestCaseExpectationDto} from '../../../model/dto/entity/logic-flow-test-case-expectation.dto';
import {TestExpectationTypeEnum} from '../../../enum/test-expectation-type.enum';
import {ComparisonTypeEnum} from '../../../enum/comparison-type.enum';
import {LogicFlowTestResultDto} from '../../../model/dto/api/logic-flow-test-result.dto';

/**
 * Response DTO for a single test case run result.
 * This mirrors what the backend POST /logicFlowTestCases/{id}/run returns.
 */
interface TestCaseRunResult {
    success: boolean;
    durationMs: number;
    executionId?: number;
    failedExpectations?: string[];
    messages?: string[];
}

/**
 * Response DTO for a bulk test suite run.
 * Mirrors backend LogicFlowTestSuiteResultDto.
 */
interface TestSuiteRunResult {
    totalTests: number;
    passed: number;
    failed: number;
    results: TestCaseRunResult[];
}

@Component({
    selector: 'mvs-lg-test-runner',
    templateUrl: './lg-test-runner.component.html',
    styleUrls: ['./lg-test-runner.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LgTestRunnerComponent implements OnInit, OnChanges, OnDestroy {

    // --- State flags ---
    busy: boolean = false;
    initialized: boolean = false;
    loadingInputs: boolean = false;
    loadingExpectations: boolean = false;
    runningTestId: number | null = null;
    runningAllTests: boolean = false;
    errorMessage: string | null = null;

    // --- Inputs ---
    @Input() flowId: number;

    // --- Outputs ---
    @Output() testCompleted = new EventEmitter<{ testCaseId: number; success: boolean }>();

    // --- Data ---
    testCases: LogicFlowTestCaseDto[] = [];
    selectedTestCase: LogicFlowTestCaseDto | null = null;
    testCaseInputs: LogicFlowTestCaseInputDto[] = [];
    testCaseExpectations: LogicFlowTestCaseExpectationDto[] = [];

    // --- Results ---
    testResult: TestCaseRunResult | null = null;
    suiteResult: TestSuiteRunResult | null = null;
    /** Map of testCaseId -> individual result from the suite run */
    suiteResultMap: Map<number, TestCaseRunResult> = new Map();

    // --- Expanded test case IDs for suite results ---
    expandedSuiteResultIds: Set<number> = new Set();

    // --- Enum references for template ---
    readonly ExpectationType = TestExpectationTypeEnum;
    readonly ComparisonType = ComparisonTypeEnum;

    // --- Subscriptions ---
    private subscriptions: Subscription[] = [];

    // --- Expectation type labels ---
    readonly expectationTypeLabels: Record<number, string> = {
        [TestExpectationTypeEnum.FLOW_VARIABLE_EQUALS]: 'Flussvariable gleich',
        [TestExpectationTypeEnum.FLOW_VARIABLE_NOT_NULL]: 'Flussvariable nicht null',
        [TestExpectationTypeEnum.ENTITY_CREATED]: 'Entität erstellt',
        [TestExpectationTypeEnum.ENTITY_UPDATED]: 'Entität aktualisiert',
        [TestExpectationTypeEnum.ENTITY_COUNT]: 'Entitätsanzahl',
        [TestExpectationTypeEnum.MESSAGE_EMITTED]: 'Nachricht gesendet',
        [TestExpectationTypeEnum.MESSAGE_COUNT]: 'Nachrichtenanzahl',
        [TestExpectationTypeEnum.NO_ERRORS]: 'Keine Fehler',
        [TestExpectationTypeEnum.STEP_EXECUTED]: 'Schritt ausgeführt',
        [TestExpectationTypeEnum.STEP_SKIPPED]: 'Schritt übersprungen',
        [TestExpectationTypeEnum.CUSTOM_ASSERTION]: 'Benutzerdefiniert'
    };

    // --- Comparison type labels ---
    readonly comparisonTypeLabels: Record<number, string> = {
        [ComparisonTypeEnum.EQUALS]: 'Gleich',
        [ComparisonTypeEnum.NOT_EQUALS]: 'Ungleich',
        [ComparisonTypeEnum.CONTAINS]: 'Enthält',
        [ComparisonTypeEnum.GREATER_THAN]: 'Größer als',
        [ComparisonTypeEnum.LESS_THAN]: 'Kleiner als',
        [ComparisonTypeEnum.IS_NULL]: 'Ist null',
        [ComparisonTypeEnum.IS_NOT_NULL]: 'Ist nicht null'
    };

    // --- Message severity config for test run messages ---
    readonly messageSeverityConfig: Record<number, { label: string; severity: 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' }> = {
        0: {label: 'Info', severity: 'info'},
        1: {label: 'Warnung', severity: 'warn'},
        2: {label: 'Fehler', severity: 'danger'},
        3: {label: 'Entscheidung', severity: 'contrast'}
    };

    constructor(
        protected coreService: MvsCoreService,
        protected logicFlowService: LogicFlowService,
        protected cd: ChangeDetectorRef,
        protected qlService: QlService
    ) {
    }

    ngOnInit(): void {
        this.refreshComponent();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }

        if (changes['flowId']) {
            this.resetState();
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    // =========================================================================
    // Data Loading
    // =========================================================================

    /**
     * Refresh the test case list from the backend.
     */
    refreshComponent(): void {
        if (!this.flowId) {
            return;
        }

        this.busy = true;
        this.errorMessage = null;

        const sub = this.qlService.builder()
            .query({
                name: 'testCases',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'lg.LogicFlowTestCase',
                    as: 'e'
                },
                filters: [
                    FilterCriteria.create('e.logicFlowId', FilterCriteria.cOperatorEqual, this.flowId)
                ],
                sortings: [
                    new Sorting('e.orderPriority', true),
                    new Sorting('e.name', true)
                ]
            })
            .execute()
            .subscribe({
                next: (result) => {
                    this.testCases = result.get('testCases') as LogicFlowTestCaseDto[];
                    this.busy = false;
                    this.initialized = true;
                    this.cd.markForCheck();
                },
                error: (err) => {
                    this.errorMessage = 'Fehler beim Laden der Testfälle.';
                    this.busy = false;
                    this.initialized = true;
                    this.cd.markForCheck();
                    console.error('Error loading test cases:', err);
                }
            });

        this.subscriptions.push(sub);
    }

    /**
     * Load inputs for a test case.
     */
    private loadTestCaseInputs(testCaseId: number): void {
        this.loadingInputs = true;

        const sub = this.qlService.builder()
            .query({
                name: 'testCaseInputs',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'lg.LogicFlowTestCaseInput',
                    as: 'e'
                },
                filters: [
                    FilterCriteria.create('e.testCaseId', FilterCriteria.cOperatorEqual, testCaseId)
                ],
                sortings: [
                    new Sorting('e.orderPriority', true)
                ]
            })
            .execute()
            .subscribe({
                next: (result) => {
                    this.testCaseInputs = result.get('testCaseInputs') as LogicFlowTestCaseInputDto[];
                    this.loadingInputs = false;
                    this.cd.markForCheck();
                },
                error: () => {
                    this.testCaseInputs = [];
                    this.loadingInputs = false;
                    this.cd.markForCheck();
                }
            });

        this.subscriptions.push(sub);
    }

    /**
     * Load expectations for a test case.
     */
    private loadTestCaseExpectations(testCaseId: number): void {
        this.loadingExpectations = true;

        const sub = this.qlService.builder()
            .query({
                name: 'testCaseExpectations',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'lg.LogicFlowTestCaseExpectation',
                    as: 'e'
                },
                filters: [
                    FilterCriteria.create('e.testCaseId', FilterCriteria.cOperatorEqual, testCaseId)
                ],
                sortings: [
                    new Sorting('e.orderPriority', true)
                ]
            })
            .execute()
            .subscribe({
                next: (result) => {
                    this.testCaseExpectations = result.get('testCaseExpectations') as LogicFlowTestCaseExpectationDto[];
                    this.loadingExpectations = false;
                    this.cd.markForCheck();
                },
                error: () => {
                    this.testCaseExpectations = [];
                    this.loadingExpectations = false;
                    this.cd.markForCheck();
                }
            });

        this.subscriptions.push(sub);
    }

    // =========================================================================
    // User Actions
    // =========================================================================

    /**
     * Select a test case and load its inputs/expectations.
     */
    selectTestCase(testCase: LogicFlowTestCaseDto): void {
        if (this.selectedTestCase?.id === testCase.id) {
            this.selectedTestCase = null;
            this.testCaseInputs = [];
            this.testCaseExpectations = [];
            this.testResult = null;
            this.cd.markForCheck();
            return;
        }

        this.selectedTestCase = testCase;
        this.testResult = null;
        this.loadTestCaseInputs(testCase.id);
        this.loadTestCaseExpectations(testCase.id);
    }

    /**
     * Run a single test case.
     */
    runTestCase(testCase: LogicFlowTestCaseDto, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }

        if (this.runningTestId !== null || this.runningAllTests) {
            return;
        }

        this.runningTestId = testCase.id;
        this.testResult = null;

        // Select the test case if not already selected
        if (this.selectedTestCase?.id !== testCase.id) {
            this.selectTestCase(testCase);
        }

        const sub = this.logicFlowService.runTestCase(testCase.id).subscribe({
            next: (dto: LogicFlowTestResultDto) => {
                const result: TestCaseRunResult = {
                    success: dto.success ?? false,
                    durationMs: dto.durationMs ?? 0,
                    executionId: dto.executionId,
                    failedExpectations: dto.failedExpectations?.map(e => e.description || e.toString()),
                    messages: dto.messages?.map(m => m.messageText || m.toString())
                };
                this.testResult = result;
                this.runningTestId = null;

                // Update test case with latest run info
                const tc = this.testCases.find(t => t.id === testCase.id);
                if (tc) {
                    tc.lastRunAt = new Date().toISOString();
                    tc.lastRunSuccess = result.success;
                    tc.lastRunDurationMs = result.durationMs;
                    tc.lastRunErrorMessage = result.failedExpectations?.join('; ') || null;
                }

                this.testCompleted.emit({testCaseId: testCase.id, success: result.success});
                this.cd.markForCheck();
            },
            error: (err) => {
                this.testResult = {
                    success: false,
                    durationMs: 0,
                    failedExpectations: [err?.error?.message || err?.message || 'Unbekannter Fehler beim Ausführen des Tests.']
                };
                this.runningTestId = null;
                this.testCompleted.emit({testCaseId: testCase.id, success: false});
                this.cd.markForCheck();
            }
        });

        this.subscriptions.push(sub);
    }

    /**
     * Run all test cases for the flow.
     */
    runAllTests(): void {
        if (this.runningAllTests || this.runningTestId !== null) {
            return;
        }

        this.runningAllTests = true;
        this.suiteResult = null;
        this.suiteResultMap.clear();
        this.expandedSuiteResultIds.clear();
        this.testResult = null;
        this.cd.markForCheck();

        const sub = this.logicFlowService.runAllTests(this.flowId).subscribe({
            next: (result: TestSuiteRunResult) => {
                this.suiteResult = result;

                // Build result map — backend results are ordered same as test cases
                if (result.results) {
                    for (let i = 0; i < result.results.length; i++) {
                        const tr = result.results[i];
                        const tc = this.testCases[i];
                        if (tc) {
                            this.suiteResultMap.set(tc.id, tr);
                            tc.lastRunAt = new Date().toISOString();
                            tc.lastRunSuccess = tr.success;
                            tc.lastRunDurationMs = tr.durationMs;
                            tc.lastRunErrorMessage = tr.failedExpectations?.join('; ') || null;
                            this.testCompleted.emit({testCaseId: tc.id, success: tr.success});
                        }
                    }
                }

                this.runningAllTests = false;
                this.cd.markForCheck();
            },
            error: (err) => {
                this.suiteResult = {
                    totalTests: this.testCases.length,
                    passed: 0,
                    failed: this.testCases.length,
                    results: []
                };
                this.runningAllTests = false;
                this.errorMessage = err?.error?.message || 'Fehler beim Ausführen aller Tests.';
                this.cd.markForCheck();
            }
        });

        this.subscriptions.push(sub);
    }

    /**
     * Toggle expansion of a suite result detail.
     */
    toggleSuiteResultExpansion(testCaseId: number): void {
        if (this.expandedSuiteResultIds.has(testCaseId)) {
            this.expandedSuiteResultIds.delete(testCaseId);
        } else {
            this.expandedSuiteResultIds.add(testCaseId);
        }
        this.cd.markForCheck();
    }

    /**
     * Check whether a suite result is expanded.
     */
    isSuiteResultExpanded(testCaseId: number): boolean {
        return this.expandedSuiteResultIds.has(testCaseId);
    }

    /**
     * Refresh button handler.
     */
    onRefresh(): void {
        this.refreshComponent();
    }

    /**
     * Clear suite results.
     */
    clearSuiteResults(): void {
        this.suiteResult = null;
        this.suiteResultMap.clear();
        this.expandedSuiteResultIds.clear();
        this.cd.markForCheck();
    }

    // =========================================================================
    // Helpers
    // =========================================================================

    /**
     * Reset all state on flow change.
     */
    private resetState(): void {
        this.selectedTestCase = null;
        this.testCases = [];
        this.testCaseInputs = [];
        this.testCaseExpectations = [];
        this.testResult = null;
        this.suiteResult = null;
        this.suiteResultMap.clear();
        this.expandedSuiteResultIds.clear();
        this.runningTestId = null;
        this.runningAllTests = false;
        this.errorMessage = null;
    }

    /**
     * Format duration in ms to a human-readable string.
     */
    formatDuration(durationMs: number | null | undefined): string {
        if (durationMs === null || durationMs === undefined) {
            return '-';
        }
        if (durationMs < 1000) {
            return `${durationMs} ms`;
        }
        const seconds = Math.floor(durationMs / 1000);
        const remainingMs = durationMs % 1000;
        if (seconds < 60) {
            return `${seconds},${String(remainingMs).padStart(3, '0').substring(0, 1)} s`;
        }
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes} min ${remainingSeconds} s`;
    }

    /**
     * Get expectation type label.
     */
    getExpectationTypeLabel(type: TestExpectationTypeEnum): string {
        return this.expectationTypeLabels[type] || 'Unbekannt';
    }

    /**
     * Get comparison type label.
     */
    getComparisonTypeLabel(type: ComparisonTypeEnum): string {
        return this.comparisonTypeLabels[type] || 'Unbekannt';
    }

    /**
     * Get test case name for suite result by testCaseId.
     */
    getTestCaseName(testCaseId: number): string {
        const tc = this.testCases.find(t => t.id === testCaseId);
        return tc?.name || `Testfall #${testCaseId}`;
    }

    /**
     * Get suite result for a specific test case.
     */
    getSuiteResultForTestCase(testCaseId: number): TestCaseRunResult | undefined {
        return this.suiteResultMap.get(testCaseId);
    }

    /**
     * Calculate suite pass percentage.
     */
    getSuitePassPercentage(): number {
        if (!this.suiteResult || this.suiteResult.totalTests === 0) {
            return 0;
        }
        return Math.round((this.suiteResult.passed / this.suiteResult.totalTests) * 100);
    }

    /**
     * Get message severity config safely.
     */
    getMessageSeverityConfig(severity: number): { label: string; severity: 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' } {
        return this.messageSeverityConfig[severity] || {label: 'Unbekannt', severity: 'secondary'};
    }
}
